import React from "react"
import { Text, View, StyleSheet } from "@react-pdf/renderer"
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table"

const styles = StyleSheet.create({
	table: {
		border: "1px solid #dadada",
		borderRadius: 8,
	},
	tableHeader: {
		backgroundColor: "#E2E8F0",
		borderBottomColor: "#dadada",
		fontWeight: "semibold",
		borderTopLeftRadius: 7,
		borderTopRightRadius: 7,
	},
	tableSubHeader: {
		backgroundColor: "#E2E8F0",
		borderBottomColor: "#dadada",
		fontWeight: "bold",
	},
	tableCell: {
		fontSize: 8,
		paddingVertical: 7,
		paddingHorizontal: 12,
		justifyContent: "center",
	},
	footerCell: {
		paddingVertical: 4,
		paddingHorizontal: 12,
		fontSize: 8,
		justifyContent: "center",
		fontWeight: "bold",
	},
	tableCell2: {
		fontSize: 9,
		padding: 4,
		justifyContent: "center",
	},
	tableCellLast: {},
	tableRow: {
		flexDirection: "row",
		borderBottomWidth: 1,
		borderBottomColor: "#dadada",
	},
	tableFooter: {
		flexDirection: "row",
		borderBottomLeftRadius: 7,
		borderBottomRightRadius: 7,
		backgroundColor: "#F1F5F9",
	},
	tableCellLeft: {
		alignItems: "flex-start",
	},
	tableCellRight: {
		alignItems: "flex-end",
	},
})

type SummaryReportData = {
	vehicle: string
	odometer: string
	business: string // miles
	businessValue: string // $
	rate: string
}

interface ReportSummaryTableProps {
	tableData: SummaryReportData[]
	footerData: Pick<SummaryReportData, "business" | "businessValue">
}

const columnHelper = createColumnHelper<SummaryReportData>()

const columns = [
	columnHelper.accessor("vehicle", {
		header: () => {
			return <Text>Vehicle</Text>
		},
		cell: (info) => info.getValue(),
		size: 540,
	}),
	columnHelper.accessor("odometer", {
		header: () => {
			return (
				<View style={{ flexDirection: "row", gap: 2 }}>
					<Text>Odometer</Text>
					<Text style={{ fontSize: 8, fontWeight: "normal" }}>
						(Start of Year)
					</Text>
				</View>
			)
		},
		cell: (info) => info.getValue(),
		size: 200,
	}),
	columnHelper.accessor("business", {
		header: () => {
			return <Text>Business Miles</Text>
		},
		cell: (info) => info.getValue(),
		size: 132,
	}),
	columnHelper.accessor("rate", {
		header: () => {
			return <Text style={{ textAlign: "right" }}>Rate</Text>
		},
		cell: (info) => info.getValue(),
		size: 100,
	}),
	columnHelper.accessor("businessValue", {
		header: () => {
			return <Text style={{ textAlign: "right" }}>Amount</Text>
		},
		cell: (info) => info.getValue(),
		size: 150,
	}),
]

const alignments = ["left", "left", "left", "right", "right"] as const

export const ReportSummaryTablePDF: React.FC<ReportSummaryTableProps> = ({
	tableData,
	footerData,
}) => {
	const table = useReactTable({
		data: tableData,
		columns,
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<View style={styles.table}>
			<View style={styles.tableHeader}>
				{table.getHeaderGroups().map((headerGroup) => {
					return (
						<View style={[styles.tableRow]} key={headerGroup.id}>
							{headerGroup.headers.map((header, index, headers) => (
								<View
									style={[
										styles.tableCell,
										{ width: header.getSize() },
										index === headers.length - 1
											? styles.tableCellLast
											: {},
										{ textAlign: alignments[index] },
									]}
									key={header.id}
								>
									{header.isPlaceholder
										? null
										: flexRender(
												header.column.columnDef.header,
												header.getContext()
										  )}
								</View>
							))}
						</View>
					)
				})}
			</View>
			<View>
				{table.getRowModel().rows.map((row, index) => (
					<View
						style={[
							styles.tableRow,
							index % 2 === 1 ? { backgroundColor: "#F8FAFC" } : {},
						]}
						key={row.id}
					>
						{row.getVisibleCells().map((cell, index) => (
							<View
								style={[
									styles.tableCell,
									{ width: cell.getContext().column.getSize() },
								]}
								key={cell.id}
							>
								<Text style={{ textAlign: alignments[index] }}>
									{flexRender(
										cell.column.columnDef.cell,
										cell.getContext()
									)}
								</Text>
							</View>
						))}
					</View>
				))}
			</View>

			<View style={[styles.tableFooter]}>
				{table.getHeaderGroups()[0].headers.map((header, index, headers) => {
					if (index < headers.length - 1)
						return (
							<View
								key={header.id}
								style={[
									styles.footerCell,
									{ width: header.getSize() },
									{ textAlign: alignments[index] },
								]}
							>
								{index === 0 ? <Text>Total</Text> : ""}
							</View>
						)
					return (
						<View style={[styles.footerCell, { width: header.getSize() }]}>
							<Text style={{ textAlign: alignments[4] }}>
								{footerData.businessValue}
							</Text>
						</View>
					)
				})}
			</View>
		</View>
	)
}
